import {
  Payment,
  GetTransactionsResponse,
  CalendarEvent as TransactionsCalendarEvent,
} from "./transactions";
import { Employee, SalonService, StaffService } from "./salon";
import { StaffHoursOverride } from "./scheduleAdjustment";

// View Types
export type CalendarViewType = "day" | "week" | "list" | "card";

// Appointment Statuses
export type AppointmentStatus =
  | "Requested"
  | "Accepted"
  | "Confirmed"
  | "Arrived"
  | "Completed"
  | "No Show"
  | "Declined"
  | "Cancelled";

// All possible appointment statuses (ordered for display)
export const ALL_APPOINTMENT_STATUSES: AppointmentStatus[] = [
  "Requested",
  "Accepted",
  "Confirmed",
  "Arrived",
  "Completed",
  "No Show",
  "Declined",
  "Cancelled",
];

// Statuses a user can pick from the status-badge dropdown — every status except
// the terminal, payment-driven Completed (completion is set at checkout).
export const SELECTABLE_STATUSES: AppointmentStatus[] = [
  "Requested",
  "Accepted",
  "Confirmed",
  "Arrived",
  "No Show",
  "Declined",
  "Cancelled",
];

// Statuses shown by default in day/week/list views — everything except
// Declined and Cancelled (both remain toggleable via the Filters dropdown Status column)
export const DEFAULT_SELECTED_STATUSES: AppointmentStatus[] = [
  "Requested",
  "Accepted",
  "Confirmed",
  "Arrived",
  "Completed",
  "No Show",
];

// Badge color per status (shared across the Filters dropdown Status column and Card view)
export const STATUS_COLORS: Record<AppointmentStatus, string> = {
  Requested: "yellow",
  Accepted: "burntOrange",
  Confirmed: "teal",
  Arrived: "limeGreen",
  Completed: "green",
  "No Show": "red",
  Declined: "gray",
  Cancelled: "gray",
};

// Display-only "Accepted (Unconfirmed)" state (an Accepted appointment within
// 48h of start; see guards.appointmentStatusDisplay): a darker step of the
// burntOrange brand ramp (Mantine color prop form; CSS surfaces use
// var(--mantine-color-burntOrange-8)).
export const ACCEPTED_UNCONFIRMED_COLOR = "burntOrange.8";

// Single source of truth for a status → Mantine badge color. Accepts any raw
// status string (or null/undefined) and falls back to gray for unknown values,
// so callers don't re-implement the map or the fallback. Use this everywhere a
// status badge is rendered (transaction modal, calendar pop-over, list/card
// views) instead of a local color switch.
export const getAppointmentStatusColor = (status?: string | null): string =>
  (status && STATUS_COLORS[status as AppointmentStatus]) || "gray";

// Every status the Card View board can group by.
export const CARD_VIEW_COLUMNS: AppointmentStatus[] = [
  "Requested",
  "Accepted",
  "Confirmed",
  "Arrived",
  "Completed",
  "No Show",
  "Declined",
  "Cancelled",
];

// Statuses the Card View board renders as columns. The Filters dropdown status
// selection can hide these; it never adds others. Accepted cards render in the
// Unconfirmed column (titled "Accepted (Unconfirmed)", writes "Accepted" on drop), which
// the Accepted selection controls (see CardView).
export const CARD_BOARD_COLUMNS: AppointmentStatus[] = [
  "Requested",
  "Accepted",
  "Confirmed",
  "Arrived",
  "Completed",
];

/** Grid zoom range (design handoff "Zoom": 50–200% in 25% steps). */
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 2;
export const ZOOM_STEP = 0.25;
export const clampZoom = (z: number): number =>
  Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(z / ZOOM_STEP) * ZOOM_STEP));

/** Week view display (design handoff "Display" dropdown). */
export type WeekDisplay = "combined" | "split";

// LocalStorage Keys
export const CALENDAR_STORAGE_KEYS = {
  VIEW_TYPE: "calendar_viewType",
  WEEK_DISPLAY: "calendar_weekDisplay",
  ZOOM: "calendar_zoom",
  SELECTED_STATUSES: "calendar_selectedStatuses",
  // Set once a stored status selection has had "Arrived" added (one-time
  // migration for selections saved before Arrived existed).
  SELECTED_STATUSES_ARRIVED_MIGRATED:
    "calendar_selectedStatuses_arrivedMigrated",
} as const;

// Date Range for API calls
export interface DateRange {
  start: string;
  end: string;
}

// Staff Service Mapping
export interface StaffServiceMapping {
  id: string;
  salonServiceId: string;
  staffId: string;
}

// Time Block Event
export interface TimeBlockEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resourceId: string;
  isTimeBlock: true;
  type: string;
  description?: string;
  recurrenceParentId?: string;
  recurrenceInterval?: number;
  recurrenceUnit?: string;
  recurrenceEndDate?: string;
  status?: string;
  /** The new block's grid preview while its drawer is open (never saved). */
  isTransient?: boolean;
}

// Re-export CalendarEvent from transactions.ts for compatibility
export type CalendarEvent = TransactionsCalendarEvent;

// Union type for all calendar events
export type CalendarEventType = TransactionsCalendarEvent | TimeBlockEvent;

// Event Preview Data (for popover)
export interface EventPreviewData {
  type: "appointment" | "timeblock";
  event: CalendarEventType;
  transaction?: GetTransactionsResponse;
  anchorEl: HTMLElement | null;
}

// Resource (Staff) for react-big-calendar
export interface CalendarResource {
  id: string;
  value: string;
  title: string;
  firstName: string;
  lastName: string;
  avatar: string | null;
  monday_start_time?: string;
  monday_end_time?: string;
  tuesday_start_time?: string;
  tuesday_end_time?: string;
  wednesday_start_time?: string;
  wednesday_end_time?: string;
  thursday_start_time?: string;
  thursday_end_time?: string;
  friday_start_time?: string;
  friday_end_time?: string;
  saturday_start_time?: string;
  saturday_end_time?: string;
  sunday_start_time?: string;
  sunday_end_time?: string;
}

// Slot info for clicking empty time slots
export interface SlotInfo {
  start: Date;
  end: Date;
  resourceId?: string;
  action: "select" | "click" | "doubleClick";
}

// Calendar Context State
export interface CalendarContextState {
  // Existing state
  staffServices: StaffServiceMapping[] | undefined;
  selectedArtists: string[];
  selectedService: string;
  filteredEmployees: Employee[];
  selectedDate: Date;
  selectedView: "day" | "week"; // For react-big-calendar internal view
  dateRange: DateRange;

  // New state for redesign
  viewType: CalendarViewType;
  /** Week view: all staff overlaid ("combined") or each staff member's week
   *  side by side ("split"). Persisted like viewType. */
  weekDisplay: WeekDisplay;
  /** Day / Week grid zoom, ZOOM_MIN–ZOOM_MAX in ZOOM_STEP steps; 1 = 6.5
   *  hours visible. Persisted like viewType. */
  zoom: number;
  selectedServiceTypes: string[];
  selectedStatuses: AppointmentStatus[];
  /** "Working today": restrict the staff list, calendar columns (day view), and
   *  events (both views) to staff with working hours on the viewed date. */
  workingToday: boolean;
  /** Staff ids with working hours on the viewed date (weekly + overrides). */
  workingStaffIds: Set<string>;
  /** Ad-hoc staff hours overrides for `dateRange`. CalendarContext is the only
   *  fetcher (day view shading + "Working today" both read this). */
  hoursOverrides: StaffHoursOverride[];

  // Setters
  setSelectedArtists: (artists: string[]) => void;
  setSelectedService: (service: string) => void;
  setSelectedDate: (date: Date) => void;
  setSelectedView: (view: "day" | "week") => void;
  setDateRange: (range: DateRange) => void;
  setViewType: (viewType: CalendarViewType) => void;
  setWeekDisplay: (display: WeekDisplay) => void;
  /** Clamped to ZOOM_MIN–ZOOM_MAX and rounded to ZOOM_STEP. */
  setZoom: (zoom: number) => void;
  setWorkingToday: (v: boolean) => void;
  setSelectedServiceTypes: (types: string[]) => void;
  setSelectedStatuses: (statuses: AppointmentStatus[]) => void;
}

// Props for view components
export interface CalendarViewProps {
  events: CalendarEventType[];
  onSelectEvent: (event: CalendarEventType) => void;
  onSelectSlot?: (slotInfo: SlotInfo) => void;
}

// Props for Header component
export interface HeaderProps {
  salonName: string;
  onCopyBookingLink: () => void;
}

// Card View Column
export interface CardViewColumn {
  id: string;
  title: string;
  status: AppointmentStatus;
  transactions: GetTransactionsResponse[];
}
